/**
 * DamageNumbers — Floating damage text over mobs when hit.
 *
 * Shows damage dealt with color coding:
 * - White: normal
 * - Yellow: critical
 * - Red: enemy damaged player
 * - Purple: poison/wither
 * - Green: heal
 */

import * as THREE from 'three';

export class DamageNumbers {
  constructor(scene, camera, renderer) {
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;
    this._numbers = []; // { element, worldPos, life, maxLife, velocity }

    // Create a container overlay that sits on top of everything
    this._container = document.createElement('div');
    this._container.style.cssText = `
      position:fixed; top:0; left:0; width:100%; height:100%;
      pointer-events:none; z-index:150; overflow:hidden;
    `;
    document.body.appendChild(this._container);
  }

  /**
   * Spawn a floating damage number at world position.
   * @param {THREE.Vector3} worldPos
   * @param {number} amount
   * @param {string} type - 'normal' | 'critical' | 'enemy' | 'poison' | 'heal'
   */
  spawn(worldPos, amount, type = 'normal') {
    const el = document.createElement('div');
    el.textContent = amount < 0 ? Math.abs(Math.round(amount)) : Math.round(amount);
    el.style.cssText = `
      position:absolute; font-family:'Courier New',monospace;
      font-weight:bold; font-size:${type === 'critical' ? '28px' : '20px'};
      text-shadow:2px 2px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000;
      pointer-events:none; will-change:transform,opacity;
      transform:translate(-50%,-50%);
    `;

    // Color by type
    const colors = {
      normal: '#FFFFFF',
      critical: '#FFD700',
      enemy: '#FF4444',
      poison: '#9C27B0',
      heal: '#4CAF50',
    };
    el.style.color = colors[type] || colors.normal;

    if (type === 'critical') {
      el.textContent = '¡' + el.textContent + '!';
    }
    if (type === 'heal') {
      el.textContent = '+' + el.textContent;
    }

    this._container.appendChild(el);

    this._numbers.push({
      element: el,
      worldPos: worldPos.clone ? worldPos.clone() : new THREE.Vector3(worldPos.x, worldPos.y + 1.5, worldPos.z),
      life: 1.2,
      maxLife: 1.2,
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 0.5,
        1.5,
        (Math.random() - 0.5) * 0.5,
      ),
    });
  }

  update(dt) {
    const _v = new THREE.Vector3();
    for (let i = this._numbers.length - 1; i >= 0; i--) {
      const n = this._numbers[i];
      n.life -= dt;
      if (n.life <= 0) {
        n.element.remove();
        this._numbers.splice(i, 1);
        continue;
      }

      // Physics: float up, slow down
      n.worldPos.addScaledVector(n.velocity, dt);
      n.velocity.y -= 3 * dt; // gravity
      n.velocity.multiplyScalar(0.95); // drag

      // Project to screen
      _v.copy(n.worldPos);
      _v.project(this.camera);

      if (_v.z > 1) {
        // Behind camera
        n.element.style.display = 'none';
        continue;
      }
      n.element.style.display = '';

      const x = (_v.x * 0.5 + 0.5) * window.innerWidth;
      const y = (-_v.y * 0.5 + 0.5) * window.innerHeight;
      n.element.style.left = x + 'px';
      n.element.style.top = y + 'px';

      // Fade out
      const alpha = Math.max(0, n.life / n.maxLife);
      n.element.style.opacity = alpha;
    }
  }
}
